import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Flame, CalendarCheck, TrendingUp, Dumbbell, LineChart as LineIcon } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, LineChart, Line, YAxis, Tooltip } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { tr, weekdays } from "@/data/i18n";

export default function Diary() {
  const { lang, user, loading } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [sel, setSel] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.listLogs().then(({ data }) => setLogs(data)).catch(() => {}).finally(() => setLoadingLogs(false));
  }, [user, loading, navigate]);

  const del = async (id) => {
    await api.deleteLog(id);
    setLogs((l) => l.filter((x) => x.id !== id));
    toast.success("OK");
  };

  const today = new Date();
  const startOfWeek = new Date(today);
  const dow = (today.getDay() + 6) % 7; // Mon=0
  startOfWeek.setDate(today.getDate() - dow);
  startOfWeek.setHours(0, 0, 0, 0);

  const thisWeek = logs.filter((l) => new Date(l.date) >= startOfWeek).length;

  // streak: consecutive days with at least one log, ending today or yesterday
  const dateSet = new Set(logs.map((l) => l.date));
  let streak = 0;
  let cur = new Date(today);
  const iso = (d) => d.toISOString().slice(0, 10);
  if (!dateSet.has(iso(cur))) cur.setDate(cur.getDate() - 1);
  while (dateSet.has(iso(cur))) { streak += 1; cur.setDate(cur.getDate() - 1); }

  // last 7 days chart
  const wd = weekdays(lang);
  const chart = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = iso(d);
    chart.push({ label: wd[(d.getDay() + 6) % 7], count: logs.filter((l) => l.date === key).length });
  }

  const stats = [
    { icon: <Dumbbell size={20} />, label: tr(lang, "sessions_total"), value: logs.length },
    { icon: <CalendarCheck size={20} />, label: tr(lang, "this_week"), value: thisWeek },
    { icon: <Flame size={20} />, label: tr(lang, "streak"), value: streak },
  ];

  // exercise progress (weight over time)
  const exerciseNames = [...new Set(logs.flatMap((l) => (l.exercises || []).filter((e) => e.weight).map((e) => e.name)))];
  const activeSel = sel || exerciseNames[0] || "";
  const progressMap = {};
  logs.forEach((l) => {
    (l.exercises || []).forEach((e) => {
      if (e.name === activeSel && e.weight) {
        const w = parseFloat(e.weight);
        if (!progressMap[l.date] || w > progressMap[l.date]) progressMap[l.date] = w;
      }
    });
  });
  const progress = Object.keys(progressMap).sort().map((d) => ({ date: d.slice(5), kg: progressMap[d] }));

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl">{tr(lang, "diary_title")}</h1>
      <p className="mt-2 text-zinc-500">{tr(lang, "diary_sub")}</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {stats.map((s, i) => (
          <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6" data-testid={`stat-${i}`}>
            <div className="flex items-center gap-2 text-red-400">{s.icon}<span className="text-xs font-bold uppercase tracking-wider text-zinc-400">{s.label}</span></div>
            <div className="mt-2 font-display text-4xl font-extrabold text-white">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6">
        <div className="mb-4 flex items-center gap-2 text-zinc-300"><TrendingUp size={16} className="text-red-400" /><span className="text-sm font-semibold">{tr(lang, "last7")}</span></div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "#71717a", fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {chart.map((e, i) => <Cell key={i} fill={e.count > 0 ? "#FF3B30" : "#27272a"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-zinc-300"><LineIcon size={16} className="text-red-400" /><span className="text-sm font-semibold">{tr(lang, "exercise_progress")}</span></div>
          {exerciseNames.length > 0 && (
            <select value={activeSel} onChange={(e) => setSel(e.target.value)} data-testid="progress-exercise-select"
                    className="appearance-none rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs text-white outline-none focus:border-red-500">
              {exerciseNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
        </div>
        {progress.length > 0 ? (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={progress} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#71717a", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 12, color: "#fff" }} />
                <Line type="monotone" dataKey="kg" stroke="#FF3B30" strokeWidth={3} dot={{ fill: "#FF3B30", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-zinc-600">{tr(lang, "no_progress")}</p>
        )}
      </div>

      <div className="mt-8">
        {loadingLogs ? <p className="text-zinc-500">...</p> : logs.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-zinc-800 p-12 text-center text-zinc-500">{tr(lang, "no_logs")}</div>
        ) : (
          <div className="space-y-3">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4" data-testid="log-item">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg bg-red-500/15 px-2.5 py-0.5 font-mono text-xs font-bold text-red-400">{l.date}</span>
                    <h3 className="truncate font-heading font-semibold text-white">{l.day_name}</h3>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-zinc-500">{l.plan_title}{l.notes ? ` · ${l.notes}` : ""}</div>
                </div>
                <button onClick={() => del(l.id)} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-red-500" data-testid="delete-log-btn"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
